// src/pages/Dashboard/Dashboard.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import "./Dashboard.css";
import { getStoredData } from "../../api/fetchData.jsx";


const stored = getStoredData();
const baseApi = stored[0]?.Api || "";
const API = `${baseApi}/safetyreport`;


/* ----------------------------- helpers ----------------------------- */
const toArray = (data) => (Array.isArray(data) ? data : data ? [data] : []);

const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const sumBy = (arr, key) =>
  arr.reduce((acc, it) => acc + safeNum(it?.[key]), 0);

// merge arrays (images/lists) without dupes, preserving first-seen order
const mergeArr = (arr, key) => {
  const seen = new Set();
  const out = [];
  for (const it of arr) {
    const val = it?.[key];
    if (!Array.isArray(val)) continue;
    for (const x of val) {
      const s = String(x ?? "");
      if (!s || seen.has(s)) continue;
      seen.add(s);
      out.push(x);
    }
  }
  return out;
};

const COLORS = ["#2563eb", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

/* ----------------------------- component ----------------------------- */
export default function Dashboard() {
  const [allReports, setAllReports] = useState([]); // array of safety report objects
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [projectId, setProjectId] = useState("ALL"); // selected project id ("ALL" aggregated)

  // number formatter (memoized)
  const nf = useMemo(
    () => new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }),
    []
  );

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setErr("");

    (async () => {
      try {
        const res = await axios.get(API, { signal: ac.signal });
        setAllReports(toArray(res.data));
      } catch (e) {
        if (axios.isCancel(e)) return;
        setErr(e?.message || "Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    })();

    return () => ac.abort();
  }, []);

  // unique projects (from the reports themselves)
  const projects = useMemo(() => {
    const map = new Map(); // id -> { _id, projectName, siteLocation, status }
    for (const r of allReports) {
      const p = r?.project || {};
      const id = String(p?._id || "");
      if (!id) continue;
      if (!map.has(id)) {
        map.set(id, {
          _id: id,
          projectName: p.projectName || "Unnamed Project",
          siteLocation: p.siteLocation || "",
          status: p.status || "",
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.projectName.localeCompare(b.projectName)
    );
  }, [allReports]);

  // current slice (either all or selected)
  const currentReports = useMemo(() => {
    if (projectId === "ALL") return allReports;
    return allReports.filter(
      (r) => String(r?.project?._id) === String(projectId)
    );
  }, [allReports, projectId]);

  // aggregated/derived view model for charts & KPIs
  const view = useMemo(() => {
    if (currentReports.length === 0) return null;

    // If exactly one report, prefer using it directly (avoid rounding noise)
    if (currentReports.length === 1) {
      const r = currentReports[0];
      return {
        header: {
          title: r?.project?.projectName || "Project",
          siteLocation: r?.project?.siteLocation || "—",
          status: r?.project?.status || "—",
        },
        totalManHoursWorked: safeNum(r.totalManHoursWorked),
        totalManDaysWorked: safeNum(r.totalManDaysWorked),
        fatality: safeNum(r.fatality),
        ltiCases: safeNum(r.ltiCases),
        fr: safeNum(r.fr),
        sr: safeNum(r.sr),
        mtiCases: safeNum(r.mtiCases),
        fac: safeNum(r.fac),
        dangerousOccurrences: safeNum(r.dangerousOccurrences),
        nearMissIncidents: safeNum(r.nearMissIncidents),
        safetyTrainingConductedInternal: Array.isArray(
          r.safetyTrainingConductedInternal
        )
          ? r.safetyTrainingConductedInternal
          : [],
        internalAudits: safeNum(r.internalAudits),
        externalAudits: safeNum(r.externalAudits),
        hseAuditInternal: safeNum(r.hseAuditInternal),
        hseAuditExternal: safeNum(r.hseAuditExternal),
      };
    }

    // Aggregate multiple reports
    const selected = projects.find((p) => p._id === projectId) || null;

    return {
      header: {
        title: selected ? selected.projectName : "All Projects",
        siteLocation: selected ? selected.siteLocation : "—",
        status: selected ? selected.status : "—",
      },
      totalManHoursWorked: sumBy(currentReports, "totalManHoursWorked"),
      totalManDaysWorked: sumBy(currentReports, "totalManDaysWorked"),
      fatality: sumBy(currentReports, "fatality"),
      ltiCases: sumBy(currentReports, "ltiCases"),
      fr: sumBy(currentReports, "fr"),
      sr: sumBy(currentReports, "sr"),
      mtiCases: sumBy(currentReports, "mtiCases"),
      fac: sumBy(currentReports, "fac"),
      dangerousOccurrences: sumBy(currentReports, "dangerousOccurrences"),
      nearMissIncidents: sumBy(currentReports, "nearMissIncidents"),
      safetyTrainingConductedInternal: mergeArr(
        currentReports,
        "safetyTrainingConductedInternal"
      ),
      internalAudits: sumBy(currentReports, "internalAudits"),
      externalAudits: sumBy(currentReports, "externalAudits"),
      hseAuditInternal: sumBy(currentReports, "hseAuditInternal"),
      hseAuditExternal: sumBy(currentReports, "hseAuditExternal"),
    };
  }, [currentReports, projectId, projects]);

  /* ---------- move all hooks ABOVE any early returns ---------- */
  // Memoized UI models (guard against null view)
  const kpis = useMemo(() => {
    const v = view || {};
    return [
      { label: "Total Man Hours Worked", value: v.totalManHoursWorked ?? 0 },
      { label: "Total Man Days Worked", value: v.totalManDaysWorked ?? 0 },
      { label: "Fatalities", value: v.fatality ?? 0 },
      { label: "LTI Cases", value: v.ltiCases ?? 0 },
      { label: "FR", value: v.fr ?? 0 },
      { label: "SR", value: v.sr ?? 0 },
    ];
  }, [view]);

  const incidentData = useMemo(() => {
    const v = view || {};
    return [
      { name: "LTI", value: v.ltiCases ?? 0 },
      { name: "MTI", value: v.mtiCases ?? 0 },
      { name: "FAC", value: v.fac ?? 0 },
      { name: "Dangerous Occurrences", value: v.dangerousOccurrences ?? 0 },
      { name: "Near Miss", value: v.nearMissIncidents ?? 0 },
    ];
  }, [view]);

  const auditData = useMemo(() => {
    const v = view || {};
    return [
      { name: "Internal Audits", value: v.internalAudits ?? 0 },
      { name: "External Audits", value: v.externalAudits ?? 0 },
      { name: "HSE Internal", value: v.hseAuditInternal ?? 0 },
      { name: "HSE External", value: v.hseAuditExternal ?? 0 },
    ];
  }, [view]);

  const hasIncidentData = useMemo(
    () => incidentData.some((d) => Number(d.value) > 0),
    [incidentData]
  );

  const onProjectChange = useCallback((e) => {
    setProjectId(e.target.value);
  }, []);
  /* -------------------------------------------------------------- */

  // Early returns AFTER all hooks are declared
  if (loading) return <div className="sd-loading">Loading...</div>;
  if (err) return <div className="sd-error">{err}</div>;
  if (!view) return <div className="sd-empty">No data to display.</div>;

  return (
    <div className="sd-container">
      {/* Filter bar */}
      <div className="sd-filterbar">
        <div className="sd-filter-item">
          <label htmlFor="project-select">Project</label>
          <select
            id="project-select"
            className="sd-filter-select"
            value={projectId}
            onChange={onProjectChange}
          >
            <option value="ALL">All Projects</option>
            {projects.map((p) => (
              <option key={p._id} value={p._id}>
                {p.projectName}
              </option>
            ))}
          </select>
        </div>
      </div>

      <h1 className="sd-title">{view.header.title} Analytics</h1>
      <p className="sd-subtitle">
        Location: {view.header.siteLocation} | Status: {view.header.status}
      </p>

      {/* KPI Cards */}
      <div className="sd-kpi-grid">
        {kpis.map((kpi, idx) => (
          <div className="sd-kpi-card" key={idx}>
            <h3>{nf.format(kpi.value)}</h3>
            <p>{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Incident Pie Chart */}
      <div className="sd-chart-section">
        <h2>Incident Breakdown</h2>
        {hasIncidentData ? (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={incidentData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ name, value }) => (value ? `${name} (${value})` : "")}
              >
                {incidentData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip formatter={(value) => nf.format(value)} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="sd-empty">
            No incident data for the selected scope.
          </div>
        )}
      </div>

      {/* Safety Training Images */}
      {Array.isArray(view.safetyTrainingConductedInternal) &&
        view.safetyTrainingConductedInternal.length > 0 && (
          <div className="sd-image-section">
            <h2>
              Safety Training Conducted (Internal) —{" "}
              {view.safetyTrainingConductedInternal.length}
            </h2>
            <div className="sd-image-grid">
              {view.safetyTrainingConductedInternal.map((url, idx) => (
                <img
                  key={`${url}-${idx}`}
                  src={url}
                  alt={`Safety training proof ${idx + 1}`}
                  className="sd-training-image"
                  loading="lazy"
                />
              ))}
            </div>
          </div>
        )}

      {/* Audit Summary */}
      <div className="sd-chart-section">
        <h2>Audits Summary</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={auditData}
            margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis allowDecimals={false} />
            <Tooltip formatter={(value) => nf.format(value)} />
            <Bar dataKey="value" fill="#2563eb" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
